import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCcw, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const INSTALL_DISMISSED_KEY = 'pwa_install_dismissed_at';
const DISMISS_DURATION_DAYS = 7;

function isInstallDismissed() {
    const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (!dismissedAt) return false;
    const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DURATION_DAYS;
}

export default function PwaStatusBanner() {
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [isOfflineDismissed, setIsOfflineDismissed] = useState(false);
    const [swUpdateConfig, setSwUpdateConfig] = useState(null);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isStandalone, setIsStandalone] = useState(false);
    const [installDismissed, setInstallDismissed] = useState(isInstallDismissed);

    useEffect(() => {
        // Manejo de estado Online/Offline
        const handleOnline = () => {
            setIsOffline(false);
            setIsOfflineDismissed(false);
        };
        const handleOffline = () => {
            setIsOffline(true);
            setIsOfflineDismissed(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Manejo de Actualizaciones del Service Worker (Fase 3)
        const handleSwUpdate = (event) => {
            setSwUpdateConfig(event.detail);
        };
        window.addEventListener('swUpdate', handleSwUpdate);

        // Manejo del evento de instalación (Fase 4)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Comprobar si ya está instalada
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsStandalone(true);
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('swUpdate', handleSwUpdate);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleUpdate = () => {
        if (swUpdateConfig && swUpdateConfig.waiting) {
            swUpdateConfig.waiting.postMessage({ type: 'SKIP_WAITING' });
            swUpdateConfig.waiting.addEventListener('statechange', (event) => {
                if (event.target.state === 'activated') {
                    window.location.reload();
                }
            });
        }
    };

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
        }
    };

    const handleDismissInstall = () => {
        localStorage.setItem(INSTALL_DISMISSED_KEY, Date.now().toString());
        setInstallDismissed(true);
    };

    const showInstallBanner = deferredPrompt && !isStandalone && !installDismissed;

    return (
        <>
            {/* Notificación Offline (Fase 2) */}
            {isOffline && !isOfflineDismissed && (
                <div className="fixed bottom-20 sm:bottom-6 left-1/2 transform -translate-x-1/2 w-11/12 max-w-md bg-yellow-500 text-yellow-900 px-4 py-3 flex items-start sm:items-center justify-between z-[9999] shadow-lg rounded-xl font-medium text-sm transition-all">
                    <div className="flex items-center flex-1">
                        <WifiOff className="w-5 h-5 mr-3 shrink-0" />
                        <span className="leading-tight">Estás navegando sin conexión. La información podría no estar 100% actualizada.</span>
                    </div>
                    <button
                        onClick={() => setIsOfflineDismissed(true)}
                        className="p-1 hover:bg-yellow-600 rounded-md transition-colors ml-2 shrink-0"
                        aria-label="Cerrar notificación"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Notificación de Actualización (Fase 3) */}
            {swUpdateConfig && (
                <div className="fixed bottom-4 right-4 bg-white dark:bg-zinc-800 border dark:border-zinc-700 shadow-xl rounded-lg p-4 z-[9999] flex flex-col sm:flex-row items-start sm:items-center gap-4 max-w-sm">
                    <div>
                        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">Nueva versión disponible</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Por favor, recarga para actualizar la aplicación a la última versión.</p>
                    </div>
                    <Button onClick={handleUpdate} size="sm" className="whitespace-nowrap shrink-0">
                        <RefreshCcw className="w-4 h-4 mr-2" />
                        Recargar
                    </Button>
                </div>
            )}

            {/* Botón Flotante de Instalación (A2HS - Fase 4) */}
            {showInstallBanner && (
                <div className="fixed bottom-4 left-4 z-[9999] flex items-center gap-1">
                    <Button
                        onClick={handleInstallClick}
                        variant="default"
                        className="shadow-lg rounded-full px-4"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Instalar App
                    </Button>
                    <button
                        onClick={handleDismissInstall}
                        className="p-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-full shadow-lg transition-colors"
                        aria-label="Ocultar botón de instalación"
                        title="No mostrar por 7 días"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}
        </>
    );
}
